import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  DollarSign,
  TrendingUp,
  Users,
  Package,
  Truck,
  AlertCircle,
  Download,
  ExternalLink,
  Sparkles,
} from "lucide-react";

interface StatsResponse {
  generatedAt: string;
  today?: {
    date: string;
    orders: number;
    gmv: number;
    newUsers: number;
    newListings: number;
    newListingNames: string[];
    sessions: number;
    browsers: number;
    installViews: number;
    platforms: { android: number; ios: number; web: number };
    locations: Array<{ label: string; sessions: number }>;
  };
  deliveryQuotes?: Array<{
    buyer: string;
    email: string | null;
    location: string | null;
    fee: number;
    miles: number | null;
    at: string;
  }>;
  revenue: {
    stoop: { lifetime: number; last7d: number; last30d: number; mtd: number };
    refunds: { count30d: number; amount30d: number };
    stripeBalance: { available: number; pending: number; currency: string } | null;
    deliveryFeesLifetime: number;
  };
  gmv: {
    lifetime: number;
    last7d: number;
    last30d: number;
    paidOrders: { lifetime: number; last7d: number; last30d: number };
    aov: number;
  };
  orders: {
    byStatus: Array<{ status: string; n: number }>;
    ordersLifetime: number;
    orders7d: number;
    orders30d: number;
  };
  users: {
    total: number;
    new7d: number;
    new30d: number;
    newMtd: number;
    byRole: Array<{ role: string; n: number }>;
    foundingMakersUsed: number;
    foundingMakersCap: number;
    sellers: { withStorefront: number; withStripeAccount: number; verified: number; pendingVerification: number; listing: number };
    topCities: Array<{ city: string; state: string; n: number }>;
  };
  topSellers30d: Array<{ id: string; name: string; gmv_30d: number; orders_30d: number }>;
  marketplace: {
    listings: { total: number; inStock: number; outOfStock: number };
  };
  engagement: {
    buyerDau: number;
    buyerWau: number;
    buyerMau: number;
    messages7d: number;
    messageSenders7d: number;
    newListings7d: number;
    feedbackByStatus: Array<{ status: string; n: number; n_7d: number }>;
  };
  traffic?: {
    appOpens: { today: number; last7d: number; last30d: number };
    pageViews: { last7d: number; last30d: number };
    sessions: { today: number; last7d: number; last30d: number };
    activeUsers: { dau: number; wau: number; mau: number };
    topPages30d: Array<{ path: string; views: number }>;
    platforms30d: Array<{ platform: string; sessions_30d: number }>;
  };
  analytics?: {
    errors7d?: number;
    topErrors?: Array<{ message: string; n: number }>;
    behaviorByPath?: Array<{ path: string; views: number; avgDwellS: number; avgScrollPct: number }>;
    listingFunnel?: { opened: number; aiFailed: number; published: number; failed: number };
  };
  deliveries: {
    byStatus: Array<{ status: string; n: number }>;
    completed7d: number;
    completed30d: number;
    avgDriverPay: number;
    activeDrivers: number;
    openUnaccepted: number;
  };
  recent: {
    users: Array<{ id: string; label: string; created_at: string }>;
    orders: Array<{ id: string; status: string; total: number; created_at: string; buyer: string }>;
    listings: Array<{ id: string; name: string; seller: string; created_at: string }>;
    deliveries: Array<{ id: string; order_id: string; completed_at: string; driver_pay: number }>;
    feedback: Array<{ id: string; category: string; status: string; preview: string; created_at: string }>;
  };
}

const STATUS_COLORS = [
  "#A44A2A",
  "#6E885F",
  "#C7A23B",
  "#5A7C9A",
  "#9C6F3F",
  "#7A5C8B",
  "#3A6B5A",
];

function money(n: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const min = Math.round(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  icon?: React.ComponentType<{ className?: string }>;
  accent?: string;
}) {
  return (
    <Card className={accent}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {label}
            </p>
            <p className="mt-1 font-display text-2xl font-bold text-foreground">{value}</p>
            {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
          </div>
          {Icon && <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />}
        </div>
      </CardContent>
    </Card>
  );
}

function Section({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-foreground">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function AdminDashboard() {
  const { data, isLoading, error } = useQuery<StatsResponse>({
    queryKey: ["/api/admin/stats"],
    queryFn: async () => {
      const res = await fetch("/api/admin/stats", { credentials: "include" });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Failed (${res.status})`);
      }
      return res.json();
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <div className="container mx-auto flex-1 px-4 py-8">
          <p className="text-muted-foreground">Loading stats…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <div className="container mx-auto flex-1 px-4 py-8">
          <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 text-destructive" />
            <div>
              <p className="font-semibold text-foreground">Couldn't load stats</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {(error as any)?.message || "Try again in a moment."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stripeCurrency = data.revenue.stripeBalance?.currency ?? "USD";
  const orderPieData = data.orders.byStatus.map((row, i) => ({
    name: row.status,
    value: row.n,
    color: STATUS_COLORS[i % STATUS_COLORS.length],
  }));
  const roleBarData = data.users.byRole.map((r) => ({ role: r.role || "(none)", n: r.n }));
  const deliveryPieData = data.deliveries.byStatus.map((row, i) => ({
    name: row.status,
    value: row.n,
    color: STATUS_COLORS[i % STATUS_COLORS.length],
  }));

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="container mx-auto flex-1 px-4 pb-12 pt-3">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Admin dashboard</h1>
            <p className="text-xs text-muted-foreground">
              Snapshot as of {new Date(data.generatedAt).toLocaleString()}
            </p>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <Button asChild variant="outline" size="sm">
              <Link href="/admin/users">Users</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link href="/admin/demand">Demand &amp; leads</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link href="/admin/feedback">Feedback inbox</Link>
            </Button>
          </div>
        </div>

        <Section
          title="Today at a glance"
          action={
            data.today ? (
              <span className="text-xs text-muted-foreground">{data.today.date} · real users only</span>
            ) : undefined
          }
        >
          {data.today ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard
                label="Orders"
                value={String(data.today.orders)}
                sub={data.today.gmv ? money(data.today.gmv) : "no sales yet"}
              />
              <StatCard label="New signups" value={String(data.today.newUsers)} />
              <StatCard
                label="New listings"
                value={String(data.today.newListings)}
                sub={data.today.newListingNames.slice(0, 3).join(", ") || undefined}
              />
              <StatCard
                label="Sessions"
                value={String(data.today.sessions)}
                sub={`${data.today.browsers} browsing makers/items`}
              />
              <StatCard label="Android app" value={String(data.today.platforms.android)} sub="sessions today" />
              <StatCard label="iOS app" value={String(data.today.platforms.ios)} sub="sessions today" />
              <StatCard label="Web" value={String(data.today.platforms.web)} sub="sessions today" />
              <StatCard label="Install page" value={String(data.today.installViews)} sub="views today" />
              {data.today.locations.length > 0 ? (
                <div className="col-span-2 flex flex-wrap items-center gap-2 pt-1 text-xs text-muted-foreground sm:col-span-4">
                  <span className="font-medium text-foreground">Where today's visitors are:</span>
                  {data.today.locations.map((l) => (
                    <span
                      key={l.label}
                      className="rounded-full border border-border bg-card px-2 py-0.5"
                    >
                      {l.label} · {l.sessions}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="col-span-2 pt-1 text-xs text-muted-foreground sm:col-span-4">
                  Visitor locations will populate from the next visits (coarse, IP-based).
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Today's activity is unavailable.</p>
          )}
        </Section>

        {data.deliveryQuotes && data.deliveryQuotes.length > 0 && (
          <Section
            title="Delivery fees buyers saw (7d)"
            action={
              <span className="text-xs text-muted-foreground">
                highest per buyer · reach out to offer free delivery
              </span>
            }
          >
            <Card>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {data.deliveryQuotes.map((q, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{q.buyer}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {q.email || "(no email)"}
                          {q.location ? ` · ${q.location}` : ""}
                          {q.miles != null ? ` · ${q.miles.toFixed(1)} mi` : ""}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 font-display text-lg font-bold ${
                          q.fee >= 15 ? "text-red-600" : "text-foreground"
                        }`}
                      >
                        {money(q.fee)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </Section>
        )}

        <Section title="Stoop revenue (our platform fees, not total sales)">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Lifetime" value={money(data.revenue.stoop.lifetime)} icon={DollarSign} />
            <StatCard label="Month-to-date" value={money(data.revenue.stoop.mtd)} icon={DollarSign} />
            <StatCard label="Last 30 days" value={money(data.revenue.stoop.last30d)} icon={DollarSign} />
            <StatCard label="Last 7 days" value={money(data.revenue.stoop.last7d)} icon={DollarSign} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard
              label="Stripe — available"
              value={
                data.revenue.stripeBalance
                  ? money(data.revenue.stripeBalance.available, stripeCurrency)
                  : "—"
              }
              sub="Ready to pay out"
              icon={Sparkles}
            />
            <StatCard
              label="Stripe — pending"
              value={
                data.revenue.stripeBalance
                  ? money(data.revenue.stripeBalance.pending, stripeCurrency)
                  : "—"
              }
              sub="In transit from Stripe"
              icon={Sparkles}
            />
            <StatCard
              label="Refunds (30d)"
              value={money(data.revenue.refunds.amount30d)}
              sub={`${data.revenue.refunds.count30d} refund${data.revenue.refunds.count30d === 1 ? "" : "s"}`}
              icon={TrendingUp}
            />
          </div>
        </Section>

        <Section title="Marketplace volume (total sales through Stoop)">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Total sales · lifetime" value={money(data.gmv.lifetime)} sub="GMV, all paid orders" />
            <StatCard label="Total sales · 30d" value={money(data.gmv.last30d)} />
            <StatCard label="Total sales · 7d" value={money(data.gmv.last7d)} />
            <StatCard label="Avg order value" value={money(data.gmv.aov)} sub="AOV" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard
              label="Orders lifetime"
              value={String(data.orders.ordersLifetime)}
              sub={`${data.orders.orders30d} in last 30d`}
              icon={Package}
            />
            <StatCard
              label="Active listings"
              value={String(data.marketplace.listings.inStock)}
              sub={`${data.marketplace.listings.outOfStock} out of stock`}
              icon={Package}
            />
            <StatCard
              label="Delivery fees lifetime"
              value={money(data.revenue.deliveryFeesLifetime)}
              sub="Charged to buyers (paid to drivers)"
              icon={Truck}
            />
          </div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Orders by status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={orderPieData} dataKey="value" nameKey="name" outerRadius={80} label>
                      {orderPieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </Section>

        <Section title="Users">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Total registered" value={String(data.users.total)} icon={Users} />
            <StatCard label="New (MTD)" value={String(data.users.newMtd)} icon={Users} />
            <StatCard label="New (30d)" value={String(data.users.new30d)} icon={Users} />
            <StatCard label="New (7d)" value={String(data.users.new7d)} icon={Users} />
          </div>
          {/* Maker journey, left to right, one card each stage — replaces the
              four different "seller" counts that used to be scattered here. */}
          <Link
            href="/admin/makers"
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
          >
            Maker journey
            <ExternalLink className="h-3 w-3" />
            <span className="ml-1 normal-case tracking-normal text-brand-secondary">
              see who's stalled →
            </span>
          </Link>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label="Storefronts"
              value={String(data.users.sellers.withStorefront)}
              sub="created a shop"
              icon={Package}
            />
            <StatCard
              label="Payments-ready"
              value={String(data.users.sellers.verified)}
              sub={`${data.users.sellers.pendingVerification} still finishing Stripe`}
            />
            <StatCard
              label="Actively listing"
              value={String(data.users.sellers.listing)}
              sub="have ≥1 live item"
            />
            <StatCard
              label="Founding makers"
              value={`${data.users.foundingMakersUsed} / ${data.users.foundingMakersCap}`}
              sub={`${data.users.foundingMakersCap - data.users.foundingMakersUsed} spots left`}
            />
          </div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Users by role</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={roleBarData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="role" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="n" fill="#A44A2A" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </Section>

        {data.traffic && (
          <Section title="Traffic (analytics beacon)">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="App opens today" value={String(data.traffic.appOpens.today)} />
              <StatCard label="App opens (7d)" value={String(data.traffic.appOpens.last7d)} />
              <StatCard label="App opens (30d)" value={String(data.traffic.appOpens.last30d)} />
              <StatCard label="Page views (30d)" value={String(data.traffic.pageViews.last30d)} sub={`${data.traffic.pageViews.last7d} in last 7d`} />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Active today" value={String(data.traffic.activeUsers.dau)} sub="opened the app · DAU" />
              <StatCard label="Active this week" value={String(data.traffic.activeUsers.wau)} sub="opened the app · WAU" />
              <StatCard label="Active this month" value={String(data.traffic.activeUsers.mau)} sub="opened the app · MAU" />
              <StatCard label="Sessions (30d)" value={String(data.traffic.sessions.last30d)} sub={`${data.traffic.sessions.today} today`} />
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Top pages (30d)</CardTitle>
                </CardHeader>
                <CardContent>
                  {data.traffic.topPages30d.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No page views recorded yet.</p>
                  ) : (
                    <ul className="space-y-1 text-sm">
                      {data.traffic.topPages30d.map((p) => (
                        <li key={p.path} className="flex justify-between gap-2">
                          <span className="truncate text-foreground">{p.path}</span>
                          <span className="shrink-0 font-medium text-muted-foreground">{p.views}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Sessions by platform (30d)</CardTitle>
                </CardHeader>
                <CardContent>
                  {data.traffic.platforms30d.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No sessions yet.</p>
                  ) : (
                    <ul className="space-y-1 text-sm">
                      {data.traffic.platforms30d.map((p) => (
                        <li key={p.platform} className="flex justify-between gap-2">
                          <span className="text-foreground">{p.platform}</span>
                          <span className="shrink-0 font-medium text-muted-foreground">{p.sessions_30d}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </Section>
        )}

        {data.analytics && (
          <Section title="User behavior">
            {data.analytics.listingFunnel && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Listing funnel (30d)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(() => {
                    const f = data.analytics!.listingFunnel!;
                    const max = Math.max(f.opened, 1);
                    const conv = f.opened > 0 ? Math.round((f.published / f.opened) * 100) : 0;
                    const rows: Array<{ label: string; value: number; color: string }> = [
                      { label: "Opened", value: f.opened, color: "#5A7C9A" },
                      { label: "AI couldn't auto-fill", value: f.aiFailed, color: "#C7A23B" },
                      { label: "Published", value: f.published, color: "#6E885F" },
                      { label: "Failed", value: f.failed, color: "#A44A2A" },
                    ];
                    return (
                      <>
                        {rows.map((r) => (
                          <div key={r.label} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{r.label}</span>
                              <span className="font-medium text-foreground">{r.value}</span>
                            </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${Math.min(100, (r.value / max) * 100)}%`,
                                  backgroundColor: r.color,
                                }}
                              />
                            </div>
                          </div>
                        ))}
                        <div className="pt-1 text-sm">
                          <span className="text-muted-foreground">Published / opened conversion: </span>
                          <span className="font-display text-lg font-bold text-foreground">{conv}%</span>
                        </div>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">
                    Errors (7d): {data.analytics.errors7d ?? 0}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!data.analytics.topErrors || data.analytics.topErrors.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No errors recorded.</p>
                  ) : (
                    <ul className="space-y-1 text-sm">
                      {data.analytics.topErrors.map((e, i) => (
                        <li key={i} className="flex justify-between gap-2">
                          <span className="truncate text-foreground" title={e.message}>
                            {e.message.length > 80 ? `${e.message.slice(0, 80)}…` : e.message}
                          </span>
                          <span className="shrink-0 font-medium text-muted-foreground">{e.n}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Where people spend time</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {!data.analytics.behaviorByPath || data.analytics.behaviorByPath.length === 0 ? (
                    <p className="p-4 text-sm text-muted-foreground">No behavior data yet.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-xs text-muted-foreground">
                          <th className="px-4 py-2 text-left">Path</th>
                          <th className="px-4 py-2 text-right">Views</th>
                          <th className="px-4 py-2 text-right">Avg time</th>
                          <th className="px-4 py-2 text-right">Scroll</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.analytics.behaviorByPath.map((b) => (
                          <tr key={b.path} className="border-b last:border-0">
                            <td className="px-4 py-2">
                              <span className="block max-w-[12rem] truncate text-foreground" title={b.path}>
                                {b.path}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right text-muted-foreground">{b.views}</td>
                            <td className="px-4 py-2 text-right text-muted-foreground">
                              {b.avgDwellS.toFixed(1)}s
                            </td>
                            <td className="px-4 py-2 text-right text-muted-foreground">
                              {Math.round(b.avgScrollPct)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </div>
          </Section>
        )}

        <Section title="Engagement (marketplace activity)">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Ordered today" value={String(data.engagement.buyerDau)} sub="buyers who placed an order" />
            <StatCard label="Ordered this week" value={String(data.engagement.buyerWau)} sub="buyers, last 7 days" />
            <StatCard label="Ordered this month" value={String(data.engagement.buyerMau)} sub="buyers, last 30 days" />
            <StatCard
              label="Messages (7d)"
              value={String(data.engagement.messages7d)}
              sub={`${data.engagement.messageSenders7d} unique senders`}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <StatCard
              label="New listings (7d)"
              value={String(data.engagement.newListings7d)}
              icon={Package}
            />
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Feedback by status</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {data.engagement.feedbackByStatus.map((f) => (
                    <li key={f.status} className="flex justify-between">
                      <span className="text-muted-foreground">{f.status}</span>
                      <span className="font-medium text-foreground">
                        {f.n} <span className="text-xs text-muted-foreground">({f.n_7d} new in 7d)</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </Section>

        <Section title="Top sellers (30d)">
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="px-4 py-2 text-left">Seller</th>
                    <th className="px-4 py-2 text-right">GMV (30d)</th>
                    <th className="px-4 py-2 text-right">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topSellers30d.length === 0 ? (
                    <tr>
                      <td className="px-4 py-4 text-center text-muted-foreground" colSpan={3}>
                        No sales in last 30 days
                      </td>
                    </tr>
                  ) : (
                    data.topSellers30d.map((s) => (
                      <tr key={s.id} className="border-b last:border-0">
                        <td className="px-4 py-2 text-foreground">
                          <Link href={`/u/${s.id}`} className="hover:underline">
                            {s.name}
                          </Link>
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-foreground">
                          {money(s.gmv_30d)}
                        </td>
                        <td className="px-4 py-2 text-right text-muted-foreground">
                          {s.orders_30d}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </Section>

        <Section title="Deliveries">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Active drivers" value={String(data.deliveries.activeDrivers)} icon={Truck} />
            <StatCard label="Open (no driver)" value={String(data.deliveries.openUnaccepted)} icon={AlertCircle} />
            <StatCard label="Completed (30d)" value={String(data.deliveries.completed30d)} />
            <StatCard label="Avg driver pay" value={money(data.deliveries.avgDriverPay)} />
          </div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Deliveries by status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={deliveryPieData} dataKey="value" nameKey="name" outerRadius={70} label>
                      {deliveryPieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </Section>

        <Section title="Geography (top 10 cities)">
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="px-4 py-2 text-left">City</th>
                    <th className="px-4 py-2 text-left">State</th>
                    <th className="px-4 py-2 text-right">Users</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.topCities.length === 0 ? (
                    <tr>
                      <td className="px-4 py-4 text-center text-muted-foreground" colSpan={3}>
                        No geocoded users
                      </td>
                    </tr>
                  ) : (
                    data.users.topCities.map((c, i) => (
                      <tr key={`${c.city}-${c.state}-${i}`} className="border-b last:border-0">
                        <td className="px-4 py-2 text-foreground">{c.city}</td>
                        <td className="px-4 py-2 text-muted-foreground">{c.state}</td>
                        <td className="px-4 py-2 text-right font-medium text-foreground">{c.n}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </Section>

        <Section title="Recent activity">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">New users</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {data.recent.users.map((u) => (
                    <li key={u.id} className="flex justify-between gap-2">
                      <Link href={`/u/${u.id}`} className="truncate text-foreground hover:underline">
                        {u.label}
                      </Link>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {relTime(u.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Recent orders</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {data.recent.orders.map((o) => (
                    <li key={o.id} className="flex justify-between gap-2">
                      <Link href={`/orders/${o.id}`} className="truncate text-foreground hover:underline">
                        {o.buyer} — {money(o.total)} ({o.status})
                      </Link>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {relTime(o.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">New listings</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {data.recent.listings.map((l) => (
                    <li key={l.id} className="flex justify-between gap-2">
                      <Link href={`/items/${l.id}`} className="truncate text-foreground hover:underline">
                        {l.name} <span className="text-muted-foreground">— {l.seller}</span>
                      </Link>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {relTime(l.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Completed deliveries</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {data.recent.deliveries.length === 0 ? (
                    <li className="text-muted-foreground">None yet</li>
                  ) : (
                    data.recent.deliveries.map((d) => (
                      <li key={d.id} className="flex justify-between gap-2">
                        <Link href={`/orders/${d.order_id}`} className="truncate text-foreground hover:underline">
                          Order {d.order_id.slice(0, 8)} — {money(d.driver_pay)} driver pay
                        </Link>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {relTime(d.completed_at)}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Recent feedback</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5 text-sm">
                  {data.recent.feedback.map((f) => (
                    <li key={f.id} className="border-l-2 border-muted pl-3">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {f.category} · {f.status}
                        </span>
                        <span>{relTime(f.created_at)}</span>
                      </div>
                      <p className="line-clamp-2 text-foreground">{f.preview}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </Section>

        <Section title="Exports">
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <a href="/api/admin/users.csv">
                <Download className="mr-2 h-4 w-4" />
                Users CSV
              </a>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href="/api/admin/orders.csv">
                <Download className="mr-2 h-4 w-4" />
                Orders CSV
              </a>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Stripe dashboard
              </a>
            </Button>
          </div>
        </Section>
      </div>
    </div>
  );
}
